import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, ArrowLeft, Save, X, LogOut, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

export default function AdminClientsPage() {
  const [clients, setClients] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingClient, setEditingClient] = useState(null);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [activeTab, setActiveTab] = useState('clients');
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  const emptyClient = {
    name: '',
    logo: '',
    category: 'brand',
    featured: true,
    order: 0
  };

  const emptyTestimonial = {
    client_name: '',
    client_title: '',
    client_company: '',
    quote: '',
    project_slug: '',
    featured: true,
    order: 0
  };

  useEffect(() => {
    const isAuth = sessionStorage.getItem('admin_auth');
    if (!isAuth) {
      navigate('/admin');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const [clientsRes, testimonialsRes, projectsRes] = await Promise.all([
        fetch(`${backendUrl}/api/clients`),
        fetch(`${backendUrl}/api/admin/testimonials`),
        fetch(`${backendUrl}/api/projects`)
      ]);
      
      const [clientsData, testimonialsData, projectsData] = await Promise.all([
        clientsRes.json(),
        testimonialsRes.json(),
        projectsRes.json()
      ]);
      
      setClients(clientsData);
      setTestimonials(testimonialsData);
      setProjects(projectsData);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Logo upload handler
  const handleLogoUpload = async (file) => {
    if (!file) return;

    setUploading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', 'logo');

      const response = await fetch(`${backendUrl}/api/admin/upload-image?image_type=logo`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      setEditingClient({ ...editingClient, logo: data.url });
      toast.success('Logo uploaded successfully');
    } catch (err) {
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  // Client handlers
  const handleSaveClient = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      if (editingClient.id) {
        // Update
        await fetch(`${backendUrl}/api/admin/clients/${editingClient.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(editingClient)
        });
        toast.success('Client updated');
      } else {
        // Create
        await fetch(`${backendUrl}/api/clients`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(editingClient)
        });
        toast.success('Client created');
      }
      
      setEditingClient(null);
      loadData();
    } catch (err) {
      toast.error('Failed to save client');
    }
  };

  const handleDeleteClient = async (id) => {
    if (!window.confirm('Delete this client?')) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${backendUrl}/api/clients/${id}`, { method: 'DELETE' });
      toast.success('Client deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete client');
    }
  };

  // Testimonial handlers
  const handleSaveTestimonial = async () => {
    if (!editingTestimonial.client_name || !editingTestimonial.quote) {
      toast.error('Client name and quote are required');
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      if (editingTestimonial.id) {
        // Update
        await fetch(`${backendUrl}/api/admin/testimonials/${editingTestimonial.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(editingTestimonial)
        });
        toast.success('Testimonial updated');
      } else {
        // Create
        await fetch(`${backendUrl}/api/admin/testimonials`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(editingTestimonial)
        });
        toast.success('Testimonial created');
      }
      
      setEditingTestimonial(null);
      loadData();
    } catch (err) {
      toast.error('Failed to save testimonial');
    }
  };

  const handleDeleteTestimonial = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${backendUrl}/api/admin/testimonials/${id}`, { method: 'DELETE' });
      toast.success('Testimonial deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete testimonial');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--bk-bg))] flex items-center justify-center">
        <p className="text-[hsl(var(--bk-fg-muted))]">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--bk-bg))]">
      {/* Header */}
      <header className="border-b border-[hsl(var(--bk-line))] bg-[hsl(var(--bk-bg-elev))] sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="sm" className="gap-2">
                <ArrowLeft size={16} /> Back to Admin
              </Button>
            </Link>
            <div>
              <h1 className="font-display text-2xl text-[hsl(var(--bk-fg))]">Clients & Testimonials</h1>
              <p className="text-sm text-[hsl(var(--bk-fg-muted))]">Manage client logos and testimonials</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => {
              sessionStorage.removeItem('admin_auth');
              navigate('/admin');
            }}
            className="gap-2"
          >
            <LogOut size={16} /> Logout
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-[400px] grid-cols-2 mb-6">
            <TabsTrigger value="clients">Clients ({clients.length})</TabsTrigger>
            <TabsTrigger value="testimonials">Testimonials ({testimonials.length})</TabsTrigger>
          </TabsList>

          {/* Clients Tab */}
          <TabsContent value="clients" className="space-y-6">
            <div className="flex justify-between items-center">
              <p className="text-[hsl(var(--bk-fg-muted))]">Manage client logos for the Clients & Trust section</p>
              <Button 
                onClick={() => {
                  const nextOrder = clients.length > 0 ? Math.max(...clients.map(c => c.order)) + 1 : 0;
                  setEditingClient({ ...emptyClient, order: nextOrder });
                }}
                className="gap-2"
                data-testid="add-client-btn"
              >
                <Plus size={16} /> Add Client
              </Button>
            </div>

            {/* Client List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {clients.map((client) => (
                <div 
                  key={client.id}
                  className="border border-[hsl(var(--bk-line))] rounded-lg p-4 bg-[hsl(var(--bk-bg-elev))] hover:bg-[hsl(var(--bk-bg-elev))]/80 transition-colors"
                  data-testid={`client-card-${client.id}`}
                >
                  {client.logo && (
                    <div className="mb-3 flex items-center justify-center h-16 bg-white/5 rounded">
                      <img src={client.logo} alt={client.name} className="h-12 object-contain" />
                    </div>
                  )}
                  <h3 className="font-display text-lg text-[hsl(var(--bk-fg))] mb-2">{client.name}</h3>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs px-2 py-1 rounded bg-[hsl(var(--bk-accent))]/10 text-[hsl(var(--bk-accent))]">
                      {client.category}
                    </span>
                    {client.featured && (
                      <Star size={14} className="text-[hsl(var(--bk-accent))]" fill="currentColor" />
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setEditingClient(client)}
                      className="flex-1 gap-2"
                    >
                      <Edit size={14} /> Edit
                    </Button>
                    <Button 
                      variant="destructive" 
                      size="sm" 
                      onClick={() => handleDeleteClient(client.id)}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {clients.length === 0 && (
              <div className="text-center py-12 border border-dashed border-[hsl(var(--bk-line))] rounded-lg">
                <p className="text-[hsl(var(--bk-fg-muted))] mb-4">No clients yet</p>
                <Button onClick={() => setEditingClient({ ...emptyClient, order: 0 })} className="gap-2">
                  <Plus size={16} /> Add First Client
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Testimonials Tab */}
          <TabsContent value="testimonials" className="space-y-6">
            <div className="flex justify-between items-center">
              <p className="text-[hsl(var(--bk-fg-muted))]">Manage testimonials for the Clients & Trust carousel</p>
              <Button 
                onClick={() => {
                  const nextOrder = testimonials.length > 0 ? Math.max(...testimonials.map(t => t.order)) + 1 : 0;
                  setEditingTestimonial({ ...emptyTestimonial, order: nextOrder });
                }}
                className="gap-2"
                data-testid="add-testimonial-btn"
              >
                <Plus size={16} /> Add Testimonial
              </Button>
            </div>

            {/* Testimonial List */}
            <div className="space-y-3">
              {testimonials.map((testimonial) => (
                <div 
                  key={testimonial.id}
                  className="border border-[hsl(var(--bk-line))] rounded-lg p-5 bg-[hsl(var(--bk-bg-elev))]"
                  data-testid={`testimonial-card-${testimonial.id}`}
                >
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex-1">
                      <p className="font-body text-[hsl(var(--bk-fg))] italic mb-3">"{testimonial.quote}"</p>
                      <div className="flex items-center gap-2 text-sm text-[hsl(var(--bk-fg-muted))]">
                        <strong>{testimonial.client_name}</strong>
                        {testimonial.client_title && <span>• {testimonial.client_title}</span>}
                        {testimonial.client_company && <span>• {testimonial.client_company}</span>}
                      </div>
                      {testimonial.project_slug && (
                        <p className="text-xs text-[hsl(var(--bk-fg-subtle))] mt-2">
                          Linked to project: <Link to={`/work/${testimonial.project_slug}`} className="text-[hsl(var(--bk-accent))] hover:underline">{testimonial.project_slug}</Link>
                        </p>
                      )}
                    </div>
                    {testimonial.featured && (
                      <Star size={16} className="text-[hsl(var(--bk-accent))] flex-shrink-0" fill="currentColor" />
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setEditingTestimonial(testimonial)}
                      className="gap-2"
                    >
                      <Edit size={14} /> Edit
                    </Button>
                    <Button 
                      variant="destructive" 
                      size="sm" 
                      onClick={() => handleDeleteTestimonial(testimonial.id)}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {testimonials.length === 0 && (
              <div className="text-center py-12 border border-dashed border-[hsl(var(--bk-line))] rounded-lg">
                <p className="text-[hsl(var(--bk-fg-muted))] mb-4">No testimonials yet</p>
                <Button onClick={() => setEditingTestimonial({ ...emptyTestimonial, order: 0 })} className="gap-2">
                  <Plus size={16} /> Add First Testimonial
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Client Editor Modal */}
      {editingClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={() => setEditingClient(null)}>
          <div 
            className="bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl text-[hsl(var(--bk-fg))]">
                {editingClient.id ? 'Edit Client' : 'New Client'}
              </h2>
              <button onClick={() => setEditingClient(null)} className="text-[hsl(var(--bk-fg-muted))] hover:text-[hsl(var(--bk-fg))]">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <Label>Client Name *</Label>
                <Input
                  value={editingClient.name}
                  onChange={(e) => setEditingClient({ ...editingClient, name: e.target.value })}
                  placeholder="e.g., Netflix, HBO, National Geographic"
                />
              </div>

              <div>
                <Label>Logo</Label>
                <div className="space-y-3">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleLogoUpload(file);
                    }}
                    disabled={uploading}
                  />
                  {uploading && <p className="text-xs text-[hsl(var(--bk-accent))]">Uploading...</p>}
                  {editingClient.logo && (
                    <div className="flex items-center gap-3 p-3 bg-[hsl(var(--bk-bg))] rounded border border-[hsl(var(--bk-line))]">
                      <img src={editingClient.logo} alt="Logo preview" className="h-12 object-contain bg-gray-100 px-2 rounded" />
                      <button
                        onClick={() => setEditingClient({ ...editingClient, logo: '' })}
                        className="text-xs text-red-500 hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-xs text-[hsl(var(--bk-fg-subtle))] mt-1">Upload logo image (will be automatically optimized)</p>
              </div>

              <div>
                <Label>Category</Label>
                <Select 
                  value={editingClient.category}
                  onValueChange={(value) => setEditingClient({ ...editingClient, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="brand">Brand</SelectItem>
                    <SelectItem value="agency">Agency</SelectItem>
                    <SelectItem value="studio">Studio</SelectItem>
                    <SelectItem value="filmmaker">Filmmaker</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Order</Label>
                <Input
                  type="number"
                  value={editingClient.order}
                  onChange={(e) => setEditingClient({ ...editingClient, order: parseInt(e.target.value) || 0 })}
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editingClient.featured}
                  onChange={(e) => setEditingClient({ ...editingClient, featured: e.target.checked })}
                  className="rounded"
                />
                <Label>Featured (show on homepage)</Label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button onClick={handleSaveClient} className="flex-1 gap-2">
                <Save size={16} /> Save Client
              </Button>
              <Button variant="outline" onClick={() => setEditingClient(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Testimonial Editor Modal */}
      {editingTestimonial && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={() => setEditingTestimonial(null)}>
          <div 
            className="bg-[hsl(var(--bk-bg-elev))] border border-[hsl(var(--bk-line))] rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl text-[hsl(var(--bk-fg))]">
                {editingTestimonial.id ? 'Edit Testimonial' : 'New Testimonial'}
              </h2>
              <button onClick={() => setEditingTestimonial(null)} className="text-[hsl(var(--bk-fg-muted))] hover:text-[hsl(var(--bk-fg))]">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <Label>Client Name *</Label>
                <Input
                  value={editingTestimonial.client_name}
                  onChange={(e) => setEditingTestimonial({ ...editingTestimonial, client_name: e.target.value })}
                  placeholder="e.g., John Smith"
                />
              </div>

              <div>
                <Label>Client Title</Label>
                <Input
                  value={editingTestimonial.client_title}
                  onChange={(e) => setEditingTestimonial({ ...editingTestimonial, client_title: e.target.value })}
                  placeholder="e.g., Creative Director, Producer"
                />
              </div>

              <div>
                <Label>Company</Label>
                <Input
                  value={editingTestimonial.client_company}
                  onChange={(e) => setEditingTestimonial({ ...editingTestimonial, client_company: e.target.value })}
                  placeholder="e.g., Netflix, HBO"
                />
              </div>

              <div>
                <Label>Quote *</Label>
                <Textarea
                  value={editingTestimonial.quote}
                  onChange={(e) => setEditingTestimonial({ ...editingTestimonial, quote: e.target.value })}
                  placeholder="Enter the testimonial quote..."
                  rows={4}
                />
              </div>

              <div>
                <Label>Project Link (Optional)</Label>
                <Select 
                  value={editingTestimonial.project_slug || 'none'}
                  onValueChange={(value) => setEditingTestimonial({ ...editingTestimonial, project_slug: value === 'none' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a project or none" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Project Link</SelectItem>
                    {projects.map((project) => (
                      <SelectItem key={project.slug} value={project.slug}>
                        {project.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[hsl(var(--bk-fg-subtle))] mt-1">Link this testimonial to a specific project (optional)</p>
              </div>

              <div>
                <Label>Order</Label>
                <Input
                  type="number"
                  value={editingTestimonial.order}
                  onChange={(e) => setEditingTestimonial({ ...editingTestimonial, order: parseInt(e.target.value) || 0 })}
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editingTestimonial.featured}
                  onChange={(e) => setEditingTestimonial({ ...editingTestimonial, featured: e.target.checked })}
                  className="rounded"
                />
                <Label>Featured (show in carousel)</Label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button onClick={handleSaveTestimonial} className="flex-1 gap-2">
                <Save size={16} /> Save Testimonial
              </Button>
              <Button variant="outline" onClick={() => setEditingTestimonial(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
